import {
  FilterToggle,
  FilterRange,
  FilterCheckbox,
  FilterRadio,
  Modal,
} from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";

type FilterProps = {
  isMobileOpened?: boolean;
  onClose?: () => void;
  onFilterChange: (filters: FiltersState) => void;
};

type FiltersState = {
  directJourney: boolean;
  flexibility: string[];
  sortBy: string;
  priceRange: [number, number];
};

export function FiltersResults({
  isMobileOpened,
  onClose,
  onFilterChange,
}: FilterProps) {
  const { t } = useTranslation();

  const initFlexibility = ["1", "2", "3"];
  const initSortBy = "price";
  const initPriceRange: [number, number] = [0, 0];

  const optionsSort = [
    { label: t("results.price"), value: "price" },
    { label: t("results.time"), value: "time" },
    { label: t("results.duration"), value: "duration" },
    { label: t("results.lowestEmissions"), value: "lowestEmissions" },
  ];
  const optionsFlexibility = [
    { label: t("results.NO_FLEX"), value: "1" },
    { label: t("results.SEMI_FLEX"), value: "2" },
    { label: t("results.FULL_FLEX"), value: "3" },
  ];

  const [filters, setFilters] = useState<FiltersState>({
    directJourney: false,
    flexibility: initFlexibility,
    sortBy: initSortBy,
    priceRange: initPriceRange,
  });

  const [tempFiltersMobile, setTempFiltersMobile] = useState<FiltersState>({
    directJourney: false,
    flexibility: initFlexibility,
    sortBy: initSortBy,
    priceRange: initPriceRange,
  });

  const updateFilters = (newFilters: Partial<FiltersState>) => {
    const updated = { ...filters, ...newFilters };
    setFilters(updated);
    onFilterChange(updated);
  };

  const updateFiltersMobile = () => {
    setFilters(tempFiltersMobile);
    onFilterChange(tempFiltersMobile);
    if (onClose) onClose();
  };

  const updateTempFiltersMobile = (newFilters: Partial<FiltersState>) => {
    const updated = { ...tempFiltersMobile, ...newFilters };
    setTempFiltersMobile(updated);
  };

  return (
    <>
      {/* Mobile View */}
      {isMobileOpened && onClose && (
        <Modal
          content={{ type: "confirm", title: t("results.sortAndFilter") }}
          isOpen={isMobileOpened}
          onClose={onClose}
          onConfirm={updateFiltersMobile}
          size="auto"
        >
          <div className="flex flex-col gap-6">
            <FilterRadio
              isMobile
              label={t("results.sortBy")}
              options={optionsSort}
              initValue={initSortBy}
              value={filters.sortBy}
              onChange={(value: string) =>
                updateTempFiltersMobile({ sortBy: value })
              }
            />
            <FilterToggle
              isMobile
              label={t("results.directConnection")}
              checked={filters.directJourney}
              onChange={(value) =>
                updateTempFiltersMobile({ directJourney: value })
              }
            />

            <FilterCheckbox
              label={t("results.flexibility")}
              options={optionsFlexibility}
              value={filters.flexibility}
              initValue={initFlexibility}
              onChange={(values: string[]) => {
                updateTempFiltersMobile({ flexibility: values });
              }}
            />

            <FilterRange
              isMobile
              label={t("results.price")}
              initValue={initPriceRange}
              value={filters.priceRange}
              onChange={(range: [number, number]) =>
                updateTempFiltersMobile({ priceRange: range })
              }
            />
          </div>
        </Modal>
      )}

      {/* Desktop View */}
      {!isMobileOpened && (
        <div className="flex gap-6 md:flex">
          <FilterRadio
            label={`${t("results.sortBy")} : ${optionsSort.find((o) => o.value === filters.sortBy)?.label}`}
            options={optionsSort}
            initValue={initSortBy}
            value={filters.sortBy}
            onApply={(value) => updateFilters({ sortBy: value })}
          />
          <FilterToggle
            label={t("results.directConnection")}
            checked={filters.directJourney}
            onChange={(value) => updateFilters({ directJourney: value })}
          />

          <FilterCheckbox
            label={t("results.flexibility")}
            options={optionsFlexibility}
            value={filters.flexibility}
            initValue={initFlexibility}
            onApply={(values) => {
              updateFilters({ flexibility: values });
            }}
          />

          <FilterRange
            label={t("results.price")}
            initValue={initPriceRange}
            value={filters.priceRange}
            onApply={(range) => updateFilters({ priceRange: range })}
          />
        </div>
      )}
    </>
  );
}
